import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, Trash2, Save, Lock, Unlock, Stethoscope, FlaskConical, Pill, Scissors, Gem, BedDouble,
  Wallet, Printer, FileText, Receipt, ShieldCheck, Undo2, X, HandCoins,
} from "lucide-react";
import { toast } from "sonner";

const PAYMENT_MODES = ["cash", "upi", "card", "bank_transfer", "cheque", "insurance", "panel"];

const SOURCE_ICONS = {
  service: Stethoscope, surgery: Scissors, ot: Scissors, lab: FlaskConical,
  pharmacy: Pill, implant: Gem, daycare: BedDouble, package: ShieldCheck, other: Receipt,
};

export default function IpdBill() {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const [adm, setAdm] = useState(null);
  const [patient, setPatient] = useState(null);
  const [invoice, setInvoice] = useState(null);          // existing invoice for this admission (running or final)
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountApprover, setDiscountApprover] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [advances, setAdvances] = useState([]);
  const [panelName, setPanelName] = useState("");
  const [panelCardNo, setPanelCardNo] = useState("");
  const [notes, setNotes] = useState("");
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: "", mode: "cash", reference: "", notes: "" });
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [serviceQuery, setServiceQuery] = useState("");
  const [rateCard, setRateCard] = useState([]);    // panel rate-card (item_code → override rate/discount)
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectForm, setCollectForm] = useState({ amount: "", mode: "cash", reference: "" });
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: "", mode: "cash", reason: "" });

  const load = async () => {
    const { data: aData } = await api.get(`/ipd/admissions/${admissionId}`);
    setAdm(aData.admission);
    if (aData.admission?.patient_id) {
      const p = await api.get(`/patients/${aData.admission.patient_id}`);
      setPatient(p.data.patient);
    }
    // Pull rate-card if this admission is panel-backed (override-priced services)
    if (aData.admission?.panel_company_id) {
      try {
        const { data: rc } = await api.get(`/panel/companies/${aData.admission.panel_company_id}/rate-card`);
        setRateCard(rc.items || []);
      } catch { setRateCard([]); }
    } else {
      setRateCard([]);
    }
    // Look up an existing invoice tied to this admission
    const { data: inv } = await api.get(`/billing/invoices?admission_id=${admissionId}`);
    const existing = (inv.items || [])[0];
    if (existing) {
      setInvoice(existing);
      // Hydrate stored ₹ discount → discount_pct for the UI (backend still
      // stores a flat rupee discount on each item).
      const hydrated = (existing.items || []).map((it) => {
        if (typeof it.discount_pct === "number") return it;
        const lg = (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0);
        const pct = lg > 0 ? ((parseFloat(it.discount) || 0) / lg) * 100 : 0;
        return { ...it, discount_pct: parseFloat(pct.toFixed(4)) };
      });
      setItems(hydrated);
      setDiscount(existing.discount_total || 0);
      setDiscountApprover(existing.discount_approved_by || "");
      setPaymentMode(existing.payment_mode || "cash");
      setAdvances(existing.advances || []);
      setPanelName(existing.panel_name || "");
      setPanelCardNo(existing.panel_card_no || "");
      setNotes(existing.notes || "");
    } else if (aData.admission) {
      // No invoice yet — pre-fill panel block from admission so billing stays in sync
      setPanelName(aData.admission.panel_name || "");
      setPanelCardNo(aData.admission.policy_no || "");
      if (aData.admission.payer_category && aData.admission.payer_category !== "cash") {
        setPaymentMode(aData.admission.payer_category === "insurance" ? "insurance" : "panel");
      }
    }
    // Service masters
    const { data: sv } = await api.get(`/billing/service-masters?active=true`);
    setServices(sv.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [admissionId]);

  const isFinal = invoice?.bill_state === "final";
  const today = new Date().toISOString().slice(0, 10);

  // ----- Helpers -----
  // Line discount is entered as a % (0-100). We store it under `discount_pct`
  // on the row and compute the flat rupee amount ("discount") that the
  // backend expects when we send the payload.
  const totals = useMemo(() => {
    let gross = 0, itemDisc = 0, tax = 0;
    items.forEach((it) => {
      const qty = parseFloat(it.qty) || 0;
      const rate = parseFloat(it.rate) || 0;
      const pct = parseFloat(it.discount_pct) || 0;
      const lg = qty * rate;
      const ld = lg * pct / 100;
      const lt = (lg - ld) * ((parseFloat(it.gst_percent) || 0) / 100);
      gross += lg; itemDisc += ld; tax += lt;
    });
    const billDisc = parseFloat(discount) || 0;    // still supported as flat ₹, independent
    const discountTotal = itemDisc + billDisc;
    const net = gross - discountTotal;
    const total = net + tax;
    const advancePaid = advances.filter(a => !a.cancelled).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    // Subtract any refunds already issued so the balance shown on screen matches reality.
    const refundedTotal = (invoice?.refunds || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const netPaid = advancePaid - refundedTotal;
    const balance = total - netPaid;
    const discountPct = gross > 0 ? (discountTotal / gross) * 100 : 0;
    return { gross, itemDisc, discountTotal, tax, net, total, advancePaid, refundedTotal, netPaid, balance, discountPct };
  }, [items, discount, advances, invoice]);

  const needsDiscountApproval = totals.discountPct > 10 && !discountApprover.trim();

  const autoFetch = async () => {
    if (!patient) return;
    try {
      const { data } = await api.get(`/billing/autofill/${patient.id}?admission_id=${admissionId}`);
      const fetched = data.items || [];
      if (fetched.length === 0) { toast.info("No charges found in linked modules yet."); return; }
      // Merge: skip dupes by (item_code, source_ref)
      const existingKeys = new Set(items.map((it) => `${it.item_code}|${it.source_ref || ""}`));
      const fresh = fetched.filter((f) => !existingKeys.has(`${f.item_code}|${f.source_ref || ""}`));
      setItems([...items, ...fresh]);
      toast.success(`Pulled ${fresh.length} line item(s) from clinical modules.`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addBlankRow = () => setItems([...items, { item_code: "", description: "", item_type: "service", date: today, qty: 1, rate: 0, discount_pct: 0, gst_percent: 0 }]);

  const pickService = (svc) => {
    // If a panel rate-card override exists for this service code, use it
    const override = rateCard.find(rc => rc.item_code === svc.code);
    const baseRate = override ? Number(override.rate || 0) : (svc.rate || 0);
    const discPct = override ? Number(override.discount_pct || 0) : 0;
    setItems([...items, {
      item_code: svc.code,
      description: svc.name + (override ? `  (Panel rate)` : ""),
      item_type: svc.type,
      date: today,
      qty: 1,
      rate: baseRate,
      discount_pct: discPct,
      gst_percent: svc.gst_percent || 0,
    }]);
    setServiceQuery("");
    if (override) toast.success(`Panel rate applied: ₹${baseRate}${discPct ? ` (−${discPct}%)` : ""}`);
  };

  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateRow = (i, key, value) => setItems(items.map((it, idx) => idx === i ? { ...it, [key]: value } : it));

  const save = async (state, opts = {}) => {
    if (!patient) return;
    if (items.length === 0) { toast.error("Add at least one line item."); return; }
    if (needsDiscountApproval) { toast.error("Discount > 10% needs approver email."); return; }
    setBusy(true);
    try {
      // If user is finalising AND the popup gave us a collection amount, embed it as an advance
      // BEFORE we send the final-state save — the advance endpoint refuses 409 on a finalised bill,
      // so we must include the receipt in the same payload.
      let finalAdvances = advances;
      if (state === "final" && opts.collectAmount && parseFloat(opts.collectAmount) > 0) {
        finalAdvances = [...advances, {
          amount: parseFloat(opts.collectAmount),
          mode: opts.collectMode || paymentMode || "cash",
          reference: opts.collectReference || "",
          notes: "Final-settlement collection",
        }];
      }

      const payload = {
        patient_id: patient.id, admission_id: admissionId, bill_type: "ipd", bill_state: state,
        items: items.map((it) => {
          const qty = parseInt(it.qty, 10) || 1;
          const rate = parseFloat(it.rate) || 0;
          const pct = parseFloat(it.discount_pct) || 0;
          const discAmt = +(qty * rate * pct / 100).toFixed(2);
          return {
            item_code: it.item_code || "", description: it.description, item_type: it.item_type || "service",
            date: it.date || today, qty, rate,
            discount: discAmt, discount_pct: pct,
            gst_percent: parseFloat(it.gst_percent) || 0,
            source_ref: it.source_ref || "",
          };
        }),
        discount_total: parseFloat(discount) || 0,
        discount_approved_by: discountApprover,
        payment_mode: paymentMode,
        amount_paid: 0,
        advances: finalAdvances,
        // Carry the admission's payer info onto the invoice so the backend
        // can flag panel/TPA bills as "pending_settlement" and the front-desk
        // gets the Settle CTA after finalisation.
        payer_category: adm?.payer_category || "cash",
        panel_name: panelName,
        panel_card_no: panelCardNo,
        panel_company_id: adm?.panel_company_id || "",
        policy_no: adm?.policy_no || "",
        tpa_name: adm?.tpa_name || "",
        corporate_name: adm?.corporate_name || "",
        notes,
      };
      let saved;
      if (invoice && invoice.id) {
        await api.patch(`/billing/invoices/${invoice.id}`, payload);
        saved = { ...invoice, bill_state: state };
      } else {
        const { data } = await api.post(`/billing/invoices`, payload);
        saved = data;
      }
      // Auto-refund excess advance when finalising — refund endpoint works fine on finalised bills
      if (state === "final" && opts.refundAmount && saved.id) {
        try {
          const { data: ref } = await api.post(`/billing/invoices/${saved.id}/refund`, {
            amount: parseFloat(opts.refundAmount),
            mode: opts.refundMode || "cash",
            reason: "Excess-advance refund on final bill",
          });
          if (ref?.refund_no) {
            setTimeout(() => window.open(`/print/refund-receipt/${saved.id}/${encodeURIComponent(ref.refund_no)}`, "_blank"), 600);
          }
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
      }
      setInvoice(saved);
      const successMsg = state === "final"
        ? (opts.collectAmount ? `Collected ₹${parseFloat(opts.collectAmount).toLocaleString("en-IN")} & finalised.` : "Bill finalised & locked.")
        : "Running bill saved.";
      toast.success(successMsg);
      if (state === "final") {
        setTimeout(() => window.open(`/print/invoice/${saved.id}`, "_blank"), 400);
      }
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  /** Finalise click — if pending balance > 0 OR excess advance > 0, popup first. */
  const onFinaliseClick = () => {
    if (items.length === 0) { toast.error("Add at least one line item."); return; }
    const bal = Number(totals.balance.toFixed(2));
    const payerCat = (adm?.payer_category || "cash").toLowerCase();
    const isPanel = ["panel", "insurance", "corporate", "tpa", "camp"].includes(payerCat);
    if (bal > 0 && isPanel) {
      // Panel / TPA — skip the cash-collection popup. The bill is finalised as
      // "pending_settlement"; we'll record the panel payment via Settle later.
      save("final");
    } else if (bal > 0) {
      // Cash / patient pays — ask the cashier to collect the balance now.
      setCollectForm({ amount: bal.toFixed(2), mode: paymentMode || "cash", reference: "" });
      setCollectOpen(true);
    } else if (bal < 0) {
      setRefundForm(f => ({ ...f, amount: Math.abs(bal).toFixed(2) }));
      setRefundOpen(true);
    } else {
      save("final");
    }
  };

  const confirmCollect = () => {
    const amt = parseFloat(collectForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount to collect, or use 'Finalise without collection'."); return; }
    save("final", {
      collectAmount: amt,
      collectMode: collectForm.mode,
      collectReference: collectForm.reference,
    });
    setCollectOpen(false);
    setCollectForm({ amount: "", mode: "cash", reference: "" });
  };

  const confirmRefund = () => {
    save("final", {
      refundAmount: refundForm.amount,
      refundMode: refundForm.mode,
    });
    setRefundOpen(false);
    setRefundForm({ amount: "", mode: "cash" });
  };

  /** Refund button on a finalised bill — issues a refund without changing state. */
  const issueRefund = async () => {
    if (!invoice?.id) return;
    if (!refundForm.amount || parseFloat(refundForm.amount) <= 0) { toast.error("Enter a refund amount"); return; }
    try {
      const { data: ref } = await api.post(`/billing/invoices/${invoice.id}/refund`, {
        amount: parseFloat(refundForm.amount),
        mode: refundForm.mode,
        reason: refundForm.reason || "Refund",
      });
      toast.success(`Refund ${ref?.refund_no || ""} · ₹${refundForm.amount} issued`);
      setRefundOpen(false);
      setRefundForm({ amount: "", mode: "cash", reason: "" });
      await load();
      if (ref?.refund_no) {
        window.open(`/print/refund-receipt/${invoice.id}/${encodeURIComponent(ref.refund_no)}`, "_blank");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  /** Cancel an individual advance receipt. */
  const cancelReceipt = async (idx) => {
    if (!invoice?.id) return;
    const reason = window.prompt("Cancellation reason (required):");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/billing/invoices/${invoice.id}/advance/${idx}/cancel`, { reason: reason.trim() });
      toast.success("Receipt cancelled");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addAdvance = async () => {
    if (!advanceForm.amount || parseFloat(advanceForm.amount) <= 0) { toast.error("Enter an advance amount"); return; }
    if (invoice && invoice.id) {
      try {
        await api.post(`/billing/invoices/${invoice.id}/advance`, {
          amount: parseFloat(advanceForm.amount), mode: advanceForm.mode,
          reference: advanceForm.reference, notes: advanceForm.notes,
        });
        toast.success(`Advance ₹${advanceForm.amount} recorded.`);
        setAdvanceOpen(false);
        setAdvanceForm({ amount: "", mode: "cash", reference: "", notes: "" });
        await load();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    } else {
      // No invoice yet — stash in local state, send when we save the running bill
      setAdvances([...advances, { amount: parseFloat(advanceForm.amount), mode: advanceForm.mode, reference: advanceForm.reference, notes: advanceForm.notes }]);
      setAdvanceOpen(false);
      setAdvanceForm({ amount: "", mode: "cash", reference: "", notes: "" });
      toast.success("Advance staged — will save with running bill.");
    }
  };

  const filteredServices = services.filter((s) => {
    if (!serviceQuery) return false;
    const q = serviceQuery.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q);
  }).slice(0, 8);

  if (!adm || !patient) return <div className="p-10 text-sm text-ink-muted">Loading bill…</div>;

  return (
    <div className="space-y-5 animate-fade-in pb-24" data-testid="ipd-bill-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">IPD / Day-care {adm.ipd_no && <span className="ml-1 font-mono text-[10px] text-brand">· {adm.ipd_no}</span>} · {isFinal ? "Final bill" : invoice ? "Running bill" : "New bill"}</div>
          <h1 className="font-heading text-3xl font-semibold">Bill — {patient.name}</h1>
          <div className="text-xs text-ink-muted">
            Admitted: {(adm.admitted_at || adm.admission_date || "").slice(0, 16).replace("T", " ") || "—"}
            {adm.discharged_at && <> · Discharged: {adm.discharged_at.slice(0, 16).replace("T", " ")}</>}
            {adm.ward && <> · Ward: {adm.ward}</>}
            {adm.bed_no && <> · Bed: {adm.bed_no}</>}
            {rateCard.length > 0 && <> · <span className="text-emerald-700">Panel rate-card active ({rateCard.length} items)</span></>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/app/ipd/${admissionId}`)} data-testid="bill-back-btn">← Back to admission</Button>
          {invoice && <a href={`/print/invoice/${invoice.id}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="bill-print-btn"><Printer size={14} className="mr-2" />Print</Button></a>}
          {isFinal && (
            <Button variant="outline" onClick={() => { setRefundForm({ amount: "", mode: "cash", reason: "" }); setRefundOpen(true); }} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="bill-refund-btn">
              <Undo2 size={14} className="mr-2" />Refund
            </Button>
          )}
          {isFinal && (
            <Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="bill-reopen-btn" onClick={async () => {
              const reason = window.prompt("Reopen reason (required) — this unlocks the bill and is logged in audit:");
              if (!reason || !reason.trim()) return;
              try {
                await api.post(`/billing/invoices/${invoice.id}/reopen`, { reason: reason.trim() });
                toast.success("Bill reopened to running state");
                await load();
              } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
            }}>
              <Unlock size={14} className="mr-2" />Reopen bill
            </Button>
          )}
        </div>
      </div>

      {/* Status pill */}
      {invoice && (
        <div className={`flex items-center gap-3 rounded-lg border-l-4 px-4 py-2.5 ${isFinal ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"}`} data-testid="bill-status-banner">
          {isFinal ? <Lock size={16} className="text-emerald-700" /> : <FileText size={16} className="text-amber-700" />}
          <div className="flex-1">
            <div className={`font-heading text-sm font-semibold ${isFinal ? "text-emerald-900" : "text-amber-900"}`}>{isFinal ? `Final bill — ${invoice.invoice_no}` : `Running bill — ${invoice.invoice_no}`}</div>
            <div className={`text-[11px] ${isFinal ? "text-emerald-700" : "text-amber-700"}`}>{isFinal ? "Locked. Use refund for corrections." : (invoice.last_reopened_at ? `Reopened ${invoice.last_reopened_at.slice(0, 16).replace("T", " ")} by ${invoice.last_reopened_by} · ${invoice.last_reopen_reason}` : "Provisional. Add advances or finalise to lock.")}</div>
          </div>
        </div>
      )}

      {/* Panel / TPA pending-settlement banner — shows after finalising a panel bill */}
      {invoice && ["pending_settlement", "partial_settlement"].includes(invoice.status) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm" data-testid="ipd-settle-banner">
          <span className="font-semibold text-amber-900">Awaiting panel / TPA settlement</span>
          <span className="text-amber-800">
            · Payer: <b className="capitalize">{invoice.payer_category || "panel"}</b>
            {invoice.panel_name && <> · {invoice.panel_name}</>}
            {invoice.policy_no && <> · Policy {invoice.policy_no}</>}
          </span>
          <span className="text-amber-900">
            · Outstanding <b>₹ {Number(invoice.balance || 0).toFixed(2)}</b>
          </span>
          <Link to={`/app/billing/${invoice.id}`} className="ml-auto">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="ipd-open-settle">
              <HandCoins size={14} className="mr-2" />Settle payment
            </Button>
          </Link>
        </div>
      )}

      {/* Patient block */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Patient</div><div className="font-semibold">{patient.name}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">UHID</div><div className="font-mono">{patient.uhid}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">IPD No.</div><div className="font-mono text-brand">{adm.ipd_no || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Age / Sex</div><div>{patient.age || "—"} · {patient.gender || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Mobile</div><div>{patient.mobile || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Ward / Bed</div><div>{adm.ward || "General"} {adm.bed_no ? `· ${adm.bed_no}` : ""}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Admit date</div><div>{(adm.admitted_at || adm.admission_date || "").slice(0, 10) || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Discharge date</div><div>{adm.discharged_at ? adm.discharged_at.slice(0, 10) : <span className="text-ink-muted">— (still admitted)</span>}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-ink-muted">Payer category</div><div className="capitalize">{adm.payer_category || "cash"}</div></div>
          <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Diagnosis</div><div>{adm.diagnosis || "—"}</div></div>
          <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-ink-muted">Panel / Insurance</div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={panelName} onChange={(e) => setPanelName(e.target.value)} placeholder="e.g. CGHS / Star Health / Self" className="h-8 text-xs" disabled={isFinal} data-testid="bill-panel-name" />
              <Input value={panelCardNo} onChange={(e) => setPanelCardNo(e.target.value)} placeholder="Card / Policy no." className="h-8 text-xs" disabled={isFinal} data-testid="bill-panel-card" />
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Item table */}
        <Card className="p-4 lg:col-span-2">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand">Billing items</div>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={autoFetch} disabled={isFinal} data-testid="bill-autofetch-btn">⚡ Auto-fetch all charges</Button>
              <Button size="sm" variant="outline" onClick={addBlankRow} disabled={isFinal} data-testid="bill-add-row"><Plus size={11} className="mr-1" />Blank row</Button>
            </div>
          </div>

          {/* Service master picker */}
          {!isFinal && (
            <div className="mb-3 relative" data-testid="bill-svc-picker">
              <Input value={serviceQuery} onChange={(e) => setServiceQuery(e.target.value)} placeholder="Search service / package master (e.g. Phaco, OCT, Bed)…" className="h-9" data-testid="bill-svc-search" />
              {filteredServices.length > 0 && (
                <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-white shadow-lg">
                  {filteredServices.map((s) => (
                    <button key={s.id} type="button" onClick={() => pickService(s)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary" data-testid={`bill-svc-pick-${s.code}`}>
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-[10px] text-ink-muted"><span className="font-mono">{s.code}</span> · {s.type}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">₹{(s.rate || 0).toLocaleString("en-IN")}</div>
                        {s.gst_percent > 0 && <div className="text-[10px] text-ink-muted">GST {s.gst_percent}%</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-2 py-1.5 text-left">#</th><th className="px-2 py-1.5 text-left">Code</th>
                  <th className="px-2 py-1.5 text-left">Service / item</th><th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Price</th>
                  <th className="px-2 py-1.5 text-right">Disc. %</th><th className="px-2 py-1.5 text-right">Total</th>
                  {!isFinal && <th className="px-2 py-1.5"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.length === 0 && <tr><td colSpan={9} className="px-2 py-8 text-center text-ink-muted">No items yet. Use <b>Auto-fetch</b> to pull from OPD/OT/Lab/Pharmacy/Implants, or pick from service masters above.</td></tr>}
                {items.map((it, i) => {
                  const Icon = SOURCE_ICONS[it.item_type] || Receipt;
                  const qty = parseFloat(it.qty) || 0;
                  const rate = parseFloat(it.rate) || 0;
                  const pct = parseFloat(it.discount_pct) || 0;
                  const lineGross = qty * rate;
                  const lineDisc = lineGross * pct / 100;
                  const lineTotal = (lineGross - lineDisc) * (1 + (parseFloat(it.gst_percent) || 0) / 100);
                  return (
                    <tr key={i} data-testid={`bill-row-${i}`}>
                      <td className="px-2 py-1 text-ink-muted">{i + 1}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{it.item_code || "—"}</td>
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          <Icon size={11} className="text-brand" />
                          {isFinal
                            ? <span>{it.description}</span>
                            : <Input value={it.description} onChange={(e) => updateRow(i, "description", e.target.value)} className="h-7 text-xs" />}
                        </div>
                      </td>
                      <td className="px-2 py-1 text-[11px]">
                        {isFinal ? it.date : <Input type="date" value={it.date || today} onChange={(e) => updateRow(i, "date", e.target.value)} className="h-7 w-32 text-xs" />}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {isFinal ? it.qty : <Input type="number" value={it.qty} onChange={(e) => updateRow(i, "qty", e.target.value)} className="h-7 w-14 text-right text-xs" />}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {isFinal ? `₹${(it.rate || 0).toLocaleString("en-IN")}` : <Input type="number" value={it.rate} onChange={(e) => updateRow(i, "rate", e.target.value)} className="h-7 w-20 text-right text-xs" />}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {isFinal ? (pct ? `${pct}%` : "—") : <Input type="number" min={0} max={100} step={0.01} value={it.discount_pct ?? 0} onChange={(e) => updateRow(i, "discount_pct", e.target.value)} className="h-7 w-16 text-right text-xs" data-testid={`bill-row-${i}-disc-pct`} />}
                      </td>
                      <td className="px-2 py-1 text-right font-semibold">₹{lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      {!isFinal && <td className="px-2 py-1 text-right"><Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`bill-row-remove-${i}`}><Trash2 size={11} className="text-rose-600" /></Button></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Totals & advances */}
        <Card className="p-4 lg:col-span-1">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand">Bill summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span className="font-mono">₹{totals.gross.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">Item discount</span><span className="font-mono text-rose-700" data-testid="bill-item-disc">- ₹{totals.itemDisc.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between">
              <span className="text-ink-muted">Bill discount (₹)</span>
              {isFinal ? <span className="font-mono text-rose-700">- ₹{Number(discount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                : <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-7 w-24 text-right text-xs" data-testid="bill-discount" />}
            </div>
            <div className="flex justify-between border-t border-border pt-1.5"><span>Net</span><span className="font-mono font-semibold">₹{totals.net.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="text-ink-muted">GST</span><span className="font-mono">+ ₹{totals.tax.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between border-t border-border pt-1.5"><span className="font-semibold">Total</span><span className="font-heading text-lg font-bold text-brand">₹{totals.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Advance paid</span><span className="font-mono">- ₹{totals.advancePaid.toLocaleString("en-IN")}</span></div>
            {totals.refundedTotal > 0 && (
              <>
                <div className="flex justify-between text-rose-700"><span>Refunds issued</span><span className="font-mono">+ ₹{totals.refundedTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between border-t border-dashed border-border pt-1 text-emerald-700"><span className="font-semibold">Net paid by patient</span><span className="font-mono font-semibold">₹{totals.netPaid.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
              </>
            )}
            <div className="flex justify-between border-t border-border pt-1.5"><span className="font-semibold">Balance due</span><span className={`font-heading text-lg font-bold ${Math.abs(totals.balance) < 0.01 ? "text-emerald-700" : totals.balance > 0 ? "text-rose-700" : "text-amber-700"}`} data-testid="bill-balance">₹{totals.balance.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
          </div>

          {needsDiscountApproval && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px]">
              <div className="font-semibold text-amber-900">Approval required</div>
              <div className="mt-0.5 text-amber-800">Discount is {totals.discountPct.toFixed(1)}% (&gt;10%). Enter approver email:</div>
              <Input value={discountApprover} onChange={(e) => setDiscountApprover(e.target.value)} placeholder="approver@hospital.in" className="mt-1 h-8 text-xs" data-testid="bill-discount-approver" />
            </div>
          )}

          <div className="mt-4 space-y-2">
            <Label className="text-[10px]">Payment mode (for balance)</Label>
            <Select value={paymentMode} onValueChange={setPaymentMode} disabled={isFinal}>
              <SelectTrigger className="h-9" data-testid="bill-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Advances */}
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-brand">Advance payments ({advances.length})</div>
              <Dialog open={advanceOpen} onOpenChange={setAdvanceOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" disabled={isFinal} data-testid="bill-advance-btn"><Wallet size={11} className="mr-1" />+ Advance</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Record advance payment</DialogTitle></DialogHeader>
                  <div className="form-grid">
                    <div><Label>Amount *</Label><Input type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} data-testid="advance-amount" /></div>
                    <div><Label>Mode</Label>
                      <Select value={advanceForm.mode} onValueChange={(v) => setAdvanceForm({ ...advanceForm, mode: v })}>
                        <SelectTrigger data-testid="advance-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>{["cash", "upi", "card", "bank_transfer", "cheque"].map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2"><Label>Reference (UPI / cheque no.)</Label><Input value={advanceForm.reference} onChange={(e) => setAdvanceForm({ ...advanceForm, reference: e.target.value })} /></div>
                  </div>
                  <Button onClick={addAdvance} className="mt-2 bg-brand hover:bg-brand-700" data-testid="advance-save">Save advance</Button>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-1">
              {advances.length === 0 && <div className="text-[11px] text-ink-muted">No advances yet.</div>}
              {advances.map((a, i) => {
                const rcptNo = a.receipt_no || `#${i + 1}`;
                return (
                  <div key={i} className={`flex items-center justify-between rounded px-2 py-1 text-[11px] ${a.cancelled ? "bg-rose-50 line-through text-rose-700" : "bg-secondary/60"}`} data-testid={`advance-row-${i}`}>
                    <span>
                      <span className="font-mono font-semibold text-brand">{rcptNo}</span>
                      <span className="ml-1 rounded bg-brand-50 px-1 text-[9px] font-bold uppercase text-brand">{a.receipt_type || "Advance"}</span>
                      <span className="mx-1">·</span>
                      <b>₹{(a.amount || 0).toLocaleString("en-IN")}</b> · {a.mode}{a.reference ? ` · ${a.reference}` : ""}
                      <span className="ml-1 text-ink-muted">{(a.received_at || "").slice(0, 10)}</span>
                      {a.cancelled && <span className="ml-2 rounded bg-rose-200 px-1 text-[9px] font-bold uppercase">Cancelled</span>}
                    </span>
                    {invoice?.id && (
                      <span className="flex items-center gap-1">
                        <a href={`/print/receipt/${invoice.id}/${i}`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost" className="h-6 px-2" data-testid={`receipt-print-${i}`}>
                            <Printer size={10} />
                          </Button>
                        </a>
                        {!a.cancelled && !isFinal && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-700 hover:bg-rose-50" onClick={() => cancelReceipt(i)} data-testid={`receipt-cancel-${i}`}>
                            <X size={10} />
                          </Button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Collect-balance popup (shown before finalising when balance > 0) */}
          <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Collect pending balance</DialogTitle>
              </DialogHeader>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                The bill has a pending balance of <b>₹ {totals.balance.toFixed(2)}</b>. Collect it now before finalising.
              </div>
              <div className="form-grid mt-3">
                <div>
                  <Label>Amount to collect *</Label>
                  <Input type="number" value={collectForm.amount} onChange={e => setCollectForm({ ...collectForm, amount: e.target.value })} data-testid="collect-amount" />
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={collectForm.mode} onValueChange={v => setCollectForm({ ...collectForm, mode: v })}>
                    <SelectTrigger data-testid="collect-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{["cash", "upi", "card", "bank_transfer", "cheque"].map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Reference (UPI / cheque no.)</Label>
                  <Input value={collectForm.reference} onChange={e => setCollectForm({ ...collectForm, reference: e.target.value })} />
                </div>
              </div>
              <div className="mt-3 flex justify-between">
                <Button variant="outline" onClick={() => { setCollectOpen(false); save("final"); }} data-testid="collect-skip">
                  Finalise without collection
                </Button>
                <Button onClick={confirmCollect} className="bg-brand hover:bg-brand-700" disabled={busy || !parseFloat(collectForm.amount)} data-testid="collect-confirm">
                  Collect ₹{Number(collectForm.amount || 0).toLocaleString("en-IN")} &amp; finalise
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Refund popup (excess advance OR explicit refund on finalised bill) */}
          <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">{isFinal ? "Issue refund" : "Refund excess advance"}</DialogTitle>
              </DialogHeader>
              {!isFinal && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                  Advance paid exceeds the bill by <b>₹ {Math.abs(totals.balance).toFixed(2)}</b>. Refund the excess before finalising.
                </div>
              )}
              <div className="form-grid mt-3">
                <div>
                  <Label>Refund amount *</Label>
                  <Input type="number" value={refundForm.amount} onChange={e => setRefundForm({ ...refundForm, amount: e.target.value })} data-testid="refund-amount" />
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={refundForm.mode} onValueChange={v => setRefundForm({ ...refundForm, mode: v })}>
                    <SelectTrigger data-testid="refund-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{["cash", "upi", "card", "bank_transfer", "cheque"].map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {isFinal && (
                  <div className="md:col-span-2">
                    <Label>Reason</Label>
                    <Input value={refundForm.reason} onChange={e => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="e.g. excess advance, billing correction" data-testid="refund-reason" />
                  </div>
                )}
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={isFinal ? issueRefund : confirmRefund} className="bg-rose-600 hover:bg-rose-700" disabled={busy} data-testid="refund-confirm">
                  <Undo2 size={14} className="mr-2" />{isFinal ? "Issue refund" : "Refund & finalise"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Action buttons */}
          {!isFinal && (
            <div className="mt-5 flex flex-col gap-2">
              <Button onClick={() => save("running")} disabled={busy || items.length === 0} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="bill-save-running">
                <Save size={14} className="mr-2" />{invoice ? "Update running bill" : "Save as running bill"}
              </Button>
              <Button onClick={onFinaliseClick} disabled={busy || items.length === 0 || needsDiscountApproval} className="bg-brand hover:bg-brand-700" data-testid="bill-finalise">
                <Lock size={14} className="mr-2" />Finalise &amp; print bill
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
